import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990ShcG = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 990/990EZ Schedule G: Fundraising & Gaming Activities"
          description="Explore Form 990/990EZ Schedule G for comprehensive details on nonprofit fundraising and gaming activities. Ensure transparency and IRS compliance."
          keywords="Fundraising activities, Form 990 Schedule G, Grant-making activities, Nonprofit grants management,"
          canonical="resources/tax-exempt/form-990-schedule-g/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 990{' '}
          <span className="highlight-container">
            <span className="highlight">Schedule G</span>
          </span>
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-990-schedule-g/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990 Schedule G at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <b>Purpose: Form 990 Schedule G </b>is used to report
                        <b> professional fundraising services, </b>fundraising
                        events, and gaming activities of tax-exempt
                        organizations filing <b>Form 990 or Form 990-EZ.</b>
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <b>Who Must File?</b> Organizations that answer{' '}
                        <b>"Yes"</b> to the applicable questions in{' '}
                        <b>Form 990, Part IV (lines 17-19),</b> or meet the
                        applicable <b>Form 990-EZ </b>reporting thresholds for
                        fundraising or gaming activities, generally must attach
                        <b> Schedule G.</b>
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <b>What Is Reported?</b> Schedule G reports{' '}
                        <b>
                          professional fundraising agreements, fundraising event
                          revenue and expenses, gaming income and expenses,
                        </b>{' '}
                        and other supplemental information required for these
                        activities.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <b>Reporting Threshold:</b> Organizations generally
                        complete the applicable parts of Schedule G if they
                        report
                        <b> more than $15,000</b> in professional fundraising
                        expenses, fundraising event gross income, or gaming
                        gross income, based on the applicable IRS reporting
                        requirements.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Schedule G?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Schedule G?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schg" sx={{ color: '#0202ff' }}>
                          Schedule G Filing Requirements
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#choose" sx={{ color: '#0202ff' }}>
                          Choose TaxZerone to complete your Schedule G filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What is Schedule G?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Schedule G, </b>
                  <i>
                    Information Regarding Fundraising or Gaming Activities{' '}
                  </i>{' '}
                  is a supplementary schedule filed annually by tax-exempt
                  organizations to provide detailed information about
                  fundraising events and gaming activities they organized for
                  the tax year
                </Typography>

                <Typography variant="body1" sx={{ mb: 5 }}>
                  This schedule seeks details on the revenue, expenses, and net
                  income associated with these events, providing a comprehensive
                  overview of the organization&apos;s financial landscape.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who must file Schedule G?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <Link href="/tax-exempt/" sx={{ color: '#0202ff' }}>
                    Tax-exempt organizations
                  </Link>{' '}
                  engaging in fundraising events or gaming activities must file
                  Schedule G as part of their annual reporting. This requirement
                  extends to entities falling under section 501(c) of the
                  Internal Revenue Code.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If your organization conducts activities such as auctions,
                  charity events, bingo nights, or any other fundraising
                  endeavor, Schedule G becomes a vital component of your annual
                  reporting requirements.
                </Typography>

                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 5,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Effortlessly complete your Schedule G filing requirements
                    and e-file your{' '}
                    <b>
                      {/* <Link
                        href="/tax-exempt/e-file-irs-form-990-ez/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 990-EZ
                      </Link>{' '} */}
                      exempt organization return with ease through TaxZerone!
                    </b>
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schg`}
                  >
                    Start E-filing Now
                  </Button>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="schg"
                >
                  Schedule G Filing Requirements
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schg-details-img.webp" />
                  <source srcSet="/assets/images/f990-schg-details-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schg-details-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule G showing organization details and basic information"
                    width={760}
                    height={110}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  All tax-exempt organizations that file{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990
                  </Link>{' '}
                  or{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-EZ
                  </Link>{' '}
                  returns must complete and attach Schedule G along with their
                  annual information return.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Below, we have provided Schedule G filing requirements for
                  each part.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part I - Fundraising Activities
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schg-part1-img.webp" />
                  <source srcSet="/assets/images/f990-schg-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schg-part1-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule G Part I-Fundraising Activities Line 1"
                    width={760}
                    height={114}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This question asks how your organization asked for or received
                  donations or funding during the year. Simply check all the
                  ways you used to raise funds.
                </Typography>
                <Box
                  component="ol"
                  type="a"
                  sx={{ mb: 3, '& li': { mb: 1.2 } }}
                >
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Mail solicitations:
                    </span>{' '}
                    Requesting donations through letters or other mailed
                    materials.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Internet and email solicitations:
                    </span>{' '}
                    Asking for support through emails, websites, or online
                    campaigns.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Phone solicitations:
                    </span>{' '}
                    Calling donors or supporters to request contributions.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      In-person solicitations:
                    </span>{' '}
                    Asking for donations face-to-face, such as during meetings
                    or visits.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Nongovernment grants:
                    </span>{' '}
                    Applying for funding from private foundations, companies, or
                    other non-government sources.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Government grants:</span>{' '}
                    Applying for funding from federal, state, or local
                    government agencies.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Special fundraising events:
                    </span>{' '}
                    Raising money through events like dinners, auctions, or
                    charity programs.
                  </li>
                </Box>
                <picture>
                  <source srcSet="/assets/images/f990-schg-part1-line2-img.webp" />
                  <source srcSet="/assets/images/f990-schg-part1-line2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schg-part1-line2-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule G, Part I, reporting fundraising activities, event details, and related revenue and expenses. Line 2"
                    width={760}
                    height={503}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This question asks whether your organization{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    had any agreement (written or verbal)
                  </span>{' '}
                  with a professional fundraiser during the year. This includes
                  outside fundraising companies or even individuals connected to
                  the organization who were hired to help raise funds.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Check <span style={{ fontWeight: 600 }}>“Yes”</span> if you
                    hired someone to provide fundraising services.
                  </li>
                  <li>
                    Check <span style={{ fontWeight: 600 }}>“No”</span> if you
                    didn’t use any professional fundraiser.
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your organization hired professional fundraisers and paid
                  any of them $5,000 or more during the year, list up to the 10
                  highest-paid fundraisers here.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (i){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Write the name of the person or fundraising company and their
                  business address.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (ii){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Briefly explains what they helped with, such as mail
                  campaigns, online fundraising, phone outreach, or donor data
                  support.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (iii){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Check whether the fundraiser handled donation money or had
                  permission to deposit or use it. If they did, explain the
                  details later in Part IV.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (iv){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the total amount of donations raised through that
                  fundraiser during the year. If the work hasn’t brought in
                  money yet, you can enter 0.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (v){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Report how much you paid the fundraiser, or how much they kept
                  as their fee.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (vi){' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract what the fundraiser was paid (Column v) from the
                  total raised (Column iv). This shows what your organization
                  has kept.{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schg-part1-line3-img.webp" />
                  <source srcSet="/assets/images/f990-schg-part1-line3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schg-part1-line3-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule G, Part I, reporting fundraising activities, event details, and related revenue and expenses. Line 3"
                    width={760}
                    height={240}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This line asks you to list the states where your organization
                  is allowed to ask for donations. Include any state where you
                  are registered to fundraise, licensed to solicit
                  contributions, or officially exempt from registering.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part II - Fundraising Events
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schg-part2-img.webp" />
                  <source srcSet="/assets/images/f990-schg-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schg-part2-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule G, Part II, reporting fundraising events with gross receipts over $5,000 and related income and expenses."
                    width={760}
                    height={427}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Complete this part if your organization earned more than
                  $15,000 from fundraising events during the year (based on Form
                  990 or{' '}
                  <Link
                    href="/resources/tax-exempt/form-990-ez-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990-EZ
                  </Link>
                  ). Only include fundraising events that brought in more than
                  $5,000 in gross receipts. Gaming activities should not be
                  reported here; they belong to Part III.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 1{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total money received from each fundraising event
                  before subtracting any expenses or costs. Include ticket
                  sales, sponsorships, or other amounts collected. Add all
                  columns to get the total in column (d).
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (a), (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report on the second-largest fundraising event that also
                  exceeded $5,000 in gross receipts.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Count all other fundraising events that each brought in more
                  than $5,000 and report them together as one total. If there
                  were no additional events, write none.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Column (d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add together the amounts from columns (a), (b), and (c). This
                  shows the overall totals for all qualifying fundraising
                  events.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the portion of payments that are considered donations or
                  gifts, including the value of any noncash contributions
                  received during the events. Total the amounts across all
                  columns.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract Line 2 from Line 1. This shows the income from the
                  events that are not treated as a contribution before deducting
                  expenses like catering, entertainment, or other event costs.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total amount of cold hard cash handed out as prizes
                  or winnings.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter what the physical prizes (like gift baskets or
                  electronics) would actually cost if you bought them at a
                  store.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter what you spent to rent the space or any equipment needed
                  to host the event.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Every dollar spent on catering, snacks, and beverages for the
                  event.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Fees for the DJ, band, or performers, plus any wages paid to
                  event workers.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Anything else you spent on, like decorations or printing that
                  wasn't listed above; just make sure to keep your own list of
                  these.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 10
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The sum of Lines 4 through 9; this is the total "bill" for
                  running the event.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 11
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Your event income minus your total costs (Line 10). If you
                  lose money, put the number in parentheses.{' '}
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part III - Gaming
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schg-part3-img.webp" />
                  <source srcSet="/assets/images/f990-schg-part3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schg-part3-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule G, Part III, reporting gaming activities with over $15,000 in revenue and related income and expenses."
                    width={760}
                    height={319}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 0.5,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {' '}
                  Enter the amount related to the first category of gaming
                  activity listed, such as bingo.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the amount related to the second category of gaming
                  activity, such as raffles.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the amount related to any other type of gaming activity
                  conducted by the organization.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add the amounts from columns (a), (b), and (c) to show the
                  total for all gaming activities.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 1{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>
                    Enter the total income earned from each type of gaming
                    activity during the year. This should be the amount
                    collected after removing contributions or donations, but
                    before taking out any expenses.
                  </li>
                  <li>
                    Do not subtract prizes, supplies, staff payments, fees, or
                    any other costs at this stage. Simply report the full gross
                    revenue for each activity in columns (a), (b), and (c), and
                    then add them together to show the total in column (d).{' '}
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total amount of cash prizes your organization gave
                  to winners from gaming activities during the year.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the value of prizes given in items rather than cash,
                  such as gift cards, merchandise, or donated products that were
                  awarded to participants.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Include any payments made for using a location, hall, or
                  equipment needed to conduct the gaming activity.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Include any additional costs directly related to running the
                  gaming activities that are not already listed above. This may
                  cover payments to gaming staff or contractors, payroll-related
                  taxes, wagering taxes, supplies, or similar operating
                  expenses. Keep a detailed breakdown in your records for
                  support.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If any gaming activities are run by volunteers, check “Yes”
                  and enter the percentage of workers who were volunteers. This
                  is calculated by dividing the number of volunteers by the
                  total number of workers (paid and unpaid) for that activity.
                  If no volunteers were involved, check “No.”
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 7
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add up all direct expenses from lines 2 through 5 and enter
                  the total in column (d).{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 8
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Subtract the total expenses (Line 7) from the gross revenue
                  (Line 1). If expenses are higher than revenue, show the result
                  as a negative number in parentheses.{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schg-part3-line9-img.webp" />
                  <source srcSet="/assets/images/f990-schg-part3-line9-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schg-part3-line9-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule G, shows gaming revenue over $15,000 and the related expenses reported by the organization (lines 9–10)."
                    width={760}
                    height={161}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 9
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  List all states where your organization ran gaming activities
                  or asked residents to participate. If you need more room, use
                  Part IV.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 9a{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” only if your organization is officially licensed
                  or registered to run gaming in every state you listed.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 9b{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your organization isn’t licensed in any of the states
                  listed, explain why in the space provided. Use Part IV if you
                  need more room.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 10a{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check <span style={{ fontWeight: 600 }}> “Yes”</span> if any
                  gaming licenses were{' '}
                  <span style={{ fontWeight: 600 }}>
                    revoked, suspended, or terminated
                  </span>{' '}
                  during the tax year.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 10b{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For each state where a license was affected, provide details.
                  Use Part IV if you need extra space.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schg-part3-line11-img.webp" />
                  <source srcSet="/assets/images/f990-schg-part3-line11-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schg-part3-line11-img.png"
                    loading="lazy"
                    alt=" Form 990 Schedule G, Part III, reporting gaming activities with over $15,000 in revenue and related income and expenses. Line 11 to line 14."
                    width={760}
                    height={200}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 11{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if any nonmembers took part in your gaming
                  activities. Members invited guests who are covered by the
                  member are considered members, but anyone paying their own way
                  is treated as a nonmember.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 12{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if your organization is part of a trust,
                  partnership, or similar group that manages charitable gaming
                  together with other organizations.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 13a{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the percentage of gaming done in your organization’s own
                  facilities.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 13b{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the percentage done in locations you don’t own.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 14{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Provide the name and business address of the person who keeps
                  your organization’s gaming or special events books. If the
                  books are kept at the person’s home, you can just use the
                  organization’s business address.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schg-part3-line15-img.webp" />
                  <source srcSet="/assets/images/f990-schg-part3-line15-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schg-part3-line15-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule G, Part III, reporting gaming activities with over $15,000 in revenue and related income and expenses.  line 15 to line 17"
                    width={760}
                    height={407}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 15a{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if your organization has a contract with an
                  outside company or person who runs gaming on your behalf and
                  you receive revenue from them. Check “No” if you run gaming
                  entirely with your own staff.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 15b{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If “Yes” to 15a, enter how much money your organization
                  received from the gaming and how much the third party kept. If
                  there’s more than one operator, list the rest in Part IV.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 15c{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If Yes , write down the name and address of any outside
                  company or person that helps run your gaming activities. If
                  you have more than one third-party operator, you can list the
                  others in Part IV.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 16{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the name of the person who oversees the gaming
                  activities and the portion of their pay related to that work.
                  This is the person who takes care of day-to-day operations,
                  like keeping track of money, managing staff, and making
                  deposits. If more than one person shares these duties, include
                  the others in Part IV.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 17a{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check “Yes” if state law requires your organization to
                  distribute a portion of gaming proceeds to other charities or
                  use it in your own exempt activities in order to maintain a
                  valid gaming license.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 17b{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the total amount required by state law to be distributed
                  during the tax year. Break it down by state in Part IV if
                  needed.{' '}
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part IV - Supplemental Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schg-part4-img.webp" />
                  <source srcSet="/assets/images/f990-schg-part4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schg-part4-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule G, Part IV, Supplemental Information section for explanations and additional details about fundraising and gaming activities."
                    width={760}
                    height={260}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use Part IV to provide the narrative explanations required, if
                  applicable, to supplement responses to
                </Typography>
                <Box component="ul" sx={{ mb: 0, '& li': { mb: 1.2 } }}>
                  <li>Part I, line 2b, columns (iii) and (v); and</li>
                  <li>Part III, lines 9, 9b, 10b, 15b, 15c, 16, and 17b.</li>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Part IV may also be used to supplement other responses to
                  questions on Schedule G (Form 990). In Part IV, identify the
                  specific part and line number that each response supports, in
                  the order in which those parts and lines appear on Schedule G
                  (Form 990).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="choose"
                >
                  Choose TaxZerone to complete your Schedule G filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>TaxZerone</b> is your trusted IRS-authorized e-file service
                  provider. With us, experience the magic of seamless filing
                  experience and get instant updates on your filing status. At
                  TaxZerone, we&apos;re not just about e-filing; we&apos;re
                  about making the entire process effortlessly smooth!
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Our expert assistance is not just a promise; it&apos;s a
                  guarantee. From start to finish, we ensure to guide your
                  e-filing experience with ease, armed with the support you
                  need.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here&apos;s how your Form 990/990-EZ return with Schedule G
                  attachment is transmitted to the IRS in 3 simple steps!
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Provide Organization Details -</b> Choose the tax year
                    for which you want to file a return, and provide your
                    organization&apos;s details.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Preview the Return -</b> Complete Schedule G and preview
                    the information provided in the return for accuracy before
                    transmitting.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Transmit to the IRS -</b> Transmit Schedule G along with
                    your{' '}
                    <Link
                      href="/resources/tax-exempt/form-990-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      990
                    </Link>
                    /
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-ez/"
                      sx={{ color: '#0202ff' }}
                    >
                      990-EZ
                    </Link>{' '}
                    return to the IRS and get the acceptance in just a few
                    hours.
                  </li>
                </ol>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  And here&apos;s another WOW thing about us – should the IRS
                  happen to reject your information return, fear not! TaxZerone
                  makes sure you can rectify the errors and retransmit your form
                  back to the IRS, all free of cost!
                </Typography>

                {/* <Alert
                  icon={false}
                  severity="warning"
                  sx={{ mb: 5 }}
                 
                >
                  <b>Note:</b> Don&apos;t include in Part VI the names of any
                  donors, grantors, or contributors because Part VI will be made
                  available for public inspection.
                </Alert> */}

                <Box
                  component="div"
                  sx={{ mb: 2, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0, lineHeight: 1.3 }}
                      >
                        Choose TaxZerone for a seamless e-filing experience and
                        stay compliant with the IRS.
                      </Typography>
                      <Typography variant="body2">
                        Your journey to stress-free tax filing starts here!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 120, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schg`}
                      >
                        E-file Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided on this page is based on
                        guidance from the following IRS resources:
                      </Typography>
                      <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i990sg.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Schedule G (Form 990 or 990-EZ) – Supplemental
                          Information Regarding Fundraising or Gaming Activities
                        </li>{' '}
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i990.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Form 990 – Return of Organization Exempt From
                          Income Tax
                        </li>{' '}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 83,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What is the purpose of Schedule G?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" sx={{ mt: 0.5 }} itemProp="text">
                    The purpose of filing Schedule G is to provide detailed
                    information about professional fundraising services,
                    fundraising events, and gaming activities conducted by
                    tax-exempt organizations for the tax year.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. Is Schedule G mandatory for all tax-exempt organizations?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography sx={{ mt: 1 }}>
                      No, Schedule G must be filed only by organizations that
                      actively participated in fundraising and gaming
                      activities. If your exempt organization does not engage in
                      such events, the filing of Schedule G may not be
                      applicable.
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. What specific information is required on Schedule G?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Box component="div" itemProp="text">
                    <Typography sx={{ mt: 0.5, mb: 2 }}>
                      Schedule G mandates a detailed breakdown of financial
                      information related to fundraising events, activities, and
                      gaming activities. This includes reporting on revenues
                      generated, associated expenses, and the resulting net
                      income.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form990ShcG;
